import React, { useState, useEffect } from 'react';
import { Col, Container, Row } from 'reactstrap';
import LoadCounts from './LoadCounts/LoadCounts';
import WindguruWidget from './WindguruWidget/WindguruWidget';
import { saveToLocalStorage } from '../../helpers/saveToLocalStorage';
import { loadFromLocalStorage } from '../../helpers/loadFromLocalStorage';
import get from '../../services/axios/get';
import styles from './Home.module.scss';
import { useAppSelector } from '../../store/hooks';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'rssapp-list': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        id: string;
      };
    }
  }
}

interface HomeProps { }

interface DateSpecificDepotSettings {
  openTime: string;
  closeTime: string;
}

interface DepotData {
  id: string;
  alias: string;
  availableSlots?: number;
  dateSpecificSettings?: { [date: string]: DateSpecificDepotSettings };
}

const regions = [
  {
    id: 'port-elizabeth',
    name: 'Port Elizabeth',
    stationId: '51679',
    widgetId: 'wg_fwdg_51679_100_1740904932372',
  },
  {
    id: 'cape-town',
    name: 'Cape Town',
    stationId: '1216014',
    widgetId: 'wg_fwdg_1216014_100_1740905345653',
    additionalParams: { ai: "1" },
  },
  {
    id: 'durban',
    name: 'Durban',
    stationId: '208308',
    widgetId: 'wg_fwdg_208308_100_1740905873765',
  }
];

const Home: React.FC<HomeProps> = (props) => {
  const [selectedRegion, setSelectedRegion] = useState('port-elizabeth');
  const [depots, setDepots] = useState<DepotData[]>([]);
  const [isDepotsLoading, setIsDepotsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const user = useAppSelector(state => state.auth.user);

  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  const getNextFiveDays = (): string[] => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 5; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(formatDate(date));
    }
    return dates;
  };

  useEffect(() => {
    const savedRegion = loadFromLocalStorage('selectedRegion');
    if (savedRegion) {
      setSelectedRegion(savedRegion);
    }

    const script = document.createElement('script');
    script.src = 'https://widget.rss.app/v1/list.js';
    script.async = true;
    document.body.appendChild(script);

    const dates = getNextFiveDays();
    setAvailableDates(dates);
    setSelectedDate(dates[0]);

    if (user?.userType === 'admin') {
      fetchDepots();
    }

    return () => {
      document.body.removeChild(script);
    };
  }, [user?.userType]);

  const fetchDepots = () => {
    get(
      '/depot',
      { isPlugin: true },
      (res) => {
        const depotData = res.data;
        setDepots(depotData);
        setIsDepotsLoading(false);
      },
      () => {
        setIsDepotsLoading(false);
      }
    );
  };

  const handleRegionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRegion = e.target.value;
    setSelectedRegion(newRegion);
    saveToLocalStorage('selectedRegion', newRegion);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDate(e.target.value);
  };

  const getCurrentDayTimes = (depot: DepotData, date: string) => {
    const settings = depot.dateSpecificSettings?.[date];
    if (!settings) {
      return { open: '--:--', closed: '--:--' };
    }
    return { open: settings.openTime, closed: settings.closeTime };
  };

  const getAvailableSlots = (depot: DepotData): number => {
    return depot.availableSlots || 0;
  };

  const currentRegion = regions.find(region => region.id === selectedRegion) || regions[0];

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.heroSection}>
        <div className={styles.heroContent}>
          <h1>Cape Cool Dashboard</h1>
        </div>
      </div>

      <Container className={styles.contentContainer}>
        {user?.userType === 'admin' && (
          <Row className="mb-4">
            <Col xs='12'>
              <div className={styles.cardContainer}>
                <div className={styles.cardHeader}>
                  <h3>Depot Status</h3>
                  <div className={styles.headerActions}>
                    <select
                      value={selectedDate}
                      onChange={handleDateChange}
                      className={styles.dateDropdown}
                    >
                      {availableDates.map(date => (
                        <option key={date} value={date}>
                          {new Date(date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </option>
                      ))}
                    </select>
                    <div className={styles.headerBadge}>Live</div>
                  </div>
                </div>
                <div className={styles.cardBody}>
                  {isDepotsLoading ? (
                    <div className={styles.loadingText}>Loading depot information...</div>
                  ) : (
                    <div className={styles.depotContainer}>
                      <div className={styles.depotHeaders}>
                        <div>DEPOT</div>
                        <div>OPEN</div>
                        <div>CLOSE</div>
                        <div>AVAILABLE SLOTS</div>
                      </div>
                      {depots.map((depot, index) => {
                        const times = getCurrentDayTimes(depot, selectedDate);
                        const slots = getAvailableSlots(depot);
                        const shadeClass = index % 2 === 0 ? 'even' : 'odd';
                        const hasSettingsForDate = depot.dateSpecificSettings?.[selectedDate];

                        return (
                          <div key={depot.id} className={[styles.depotRow, styles[shadeClass]].join(' ')}>
                            <div className={styles.depotName}>{depot.alias}</div>
                            <div className={hasSettingsForDate ? '' : styles.noSettings}>
                              {times.open}
                            </div>
                            <div className={hasSettingsForDate ? '' : styles.noSettings}>
                              {times.closed}
                            </div>
                            <div className={`${styles.plugCount} ${hasSettingsForDate ? '' : styles.noSettings}`}>
                              {hasSettingsForDate ? (
                                `${slots}${slots > 99 ? '+' : ''}`
                              ) : (
                                'No settings'
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        )}

        {user?.userType !== 'owner' && user?.userType !== 'plugDepot' && (
          <Row className="mb-4">
            <Col xs='12'>
              <div className={styles.cardContainer}>
                <div className={styles.cardHeader}>
                  <h3>Load Statistics</h3>
                  <div className={styles.headerBadge}>Live</div>
                </div>
                <div className={styles.cardBody}>
                  <LoadCounts />
                </div>
              </div>
            </Col>
          </Row>
        )}

        <Row className={styles.dashboardCards}>
          <Col xs='12' md='6' className="d-flex mb-4 mb-md-0">
            <div className={styles.cardContainer}>
              <div className={styles.cardHeader}>
                <h3>{currentRegion.name} Wind Forecast</h3>
                <div className={styles.headerActions}>
                  <select
                    id="region-select"
                    value={selectedRegion}
                    onChange={handleRegionChange}
                    className={styles.regionDropdown}
                  >
                    {regions.map(region => (
                      <option key={region.id} value={region.id}>
                        {region.name}
                      </option>
                    ))}
                  </select>
                  <div className={styles.headerBadge}>Live</div>
                </div>
              </div>
              <div className={styles.cardBody}>
                <WindguruWidget
                  key={currentRegion.id}
                  stationId={currentRegion.stationId}
                  widgetId={currentRegion.widgetId}
                  additionalParams={currentRegion.additionalParams}
                  title={`${currentRegion.name} Wind Forecast`}
                />
              </div>
            </div>
          </Col>

          <Col xs='12' md='6' className="d-flex">
            <div className={styles.cardContainer}>
              <div className={styles.cardHeader}>
                <h3>Latest Updates</h3>
                <div className={styles.headerBadge}>RSS</div>
              </div>
              <div className={styles.cardBody}>
                <div className={styles.rssWidget}>
                  <rssapp-list id="_lzsFWxzhcqkwnVU6"></rssapp-list>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Home;